/**
 * Device detection utility for identifying device type and environment
 * Detects iOS vs Android and whether running in WebView vs browser
 */

export type DeviceType = 'ios' | 'android' | 'unknown';
export type EnvironmentType = 'webview' | 'browser' | 'unknown';

export interface DeviceInfo {
  type: DeviceType;
  environment: EnvironmentType;
  isWebView: boolean;
  isBrowser: boolean;
  userAgent: string;
}

/**
 * Detect the current device type (iOS or Android)
 */
export function getDeviceType(): DeviceType {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for iOS (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }
  
  // Check for Android
  if (/android/.test(userAgent)) {
    return 'android';
  }
  
  return 'unknown';
}

/**
 * Detect if the app is running in a WebView (native app wrapper)
 * vs a mobile browser (Safari, Chrome, etc.)
 */
export function getEnvironmentType(): EnvironmentType {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // iOS WebView detection
  // In iOS WebView: no "Safari" in UA, but has "Version" and "Mobile"
  // In iOS Safari: has "Safari" and "Mobile" or "Safari" and "iPad"
  if (/iphone|ipad|ipod/.test(userAgent)) {
    // If it's iOS, check if it's Safari or WebView
    if (/safari/.test(userAgent) && !/crios|fxios|puffin/.test(userAgent)) {
      // It's Safari or a standard browser
      return 'browser';
    }
    // It's a WebView (native app)
    return 'webview';
  }
  
  // Android WebView detection
  // Android WebView: has "Android" and "Version" but may lack Chrome indicators
  // Or explicitly contains "WebView" or specific app identifiers
  if (/android/.test(userAgent)) {
    // Check for common native app WebView patterns
    if (/react native|react-native|flutter|cordova|capacitor/.test(userAgent)) {
      return 'webview';
    }
    
    // React Native WebView
    if ((window as any).ReactNativeWebView) {
      return 'webview';
    }
    
    // Standard Android browser (Chrome, Firefox, etc.)
    return 'browser';
  }
  
  return 'unknown';
}

/**
 * Get comprehensive device information
 */
export function getDeviceInfo(): DeviceInfo {
  const type = getDeviceType();
  const environment = getEnvironmentType();
  
  return {
    type,
    environment,
    isWebView: environment === 'webview',
    isBrowser: environment === 'browser',
    userAgent: navigator.userAgent,
  };
}

/**
 * Check if we should show the mobile app download prompt
 * Only show if:
 * - Device is iOS or Android
 * - User is in a browser (not in the native WebView already)
 * - Not already installed
 */
export function shouldShowDownloadPrompt(): boolean {
  const deviceInfo = getDeviceInfo();
  
  // Only show for mobile browsers
  if (deviceInfo.type === 'unknown' || deviceInfo.environment !== 'browser') {
    return false;
  }
  
  // Check localStorage to see if user has dismissed the prompt
  const isDismissed = localStorage.getItem('mobile_app_download_prompt_dismissed');
  if (isDismissed === 'true') {
    return false;
  }
  
  return true;
}

/**
 * Mark the download prompt as dismissed
 */
export function dismissDownloadPrompt(): void {
  localStorage.setItem('mobile_app_download_prompt_dismissed', 'true');
}

/**
 * Reset the download prompt (for testing)
 */
export function resetDownloadPrompt(): void {
  localStorage.removeItem('mobile_app_download_prompt_dismissed');
}

